import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { FaCheckCircle } from "react-icons/fa";
import Input from "../inputs/Input";
import PhoneInput from "../inputs/PhoneInput";
import { ADMIN, CRM, USERS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import "./AdminRepresentativeForm.css";

const STEPS = ["Representante", "Funis", "Confirmação", "Conclusão"];

const AdminRepresentativeForm = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorQuery, setErrorQuery] = useState(false);
  const [errorFunnels, setErrorFunnels] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    control,
    formState: { errors },
    clearErrors,
  } = useForm();

  const onBlurEmailInfo = async (e) => {
    try {
      await axios.get(`${USERS.GET_USER_BY_EMAIL}/${e.target.value}`);
      clearErrors(e.target.name);
    } catch (error) {
      setError(`${e.target.name}`, {
        type: "manual",
        message: "Ja possui este campo cadastrado!",
      });
    }
  };

  const { data: funnels = [] } = useQuery(
    ["funnels-for-representative"],
    async () => {
      const { data } = await axios.get(CRM.FUNNELS);
      return data?.data || [];
    },
  );

  useEffect(() => {
    if (!Array.isArray(funnels)) return;
    funnels.forEach((funnel, idx) => {
      setValue(`funnelSelection[${idx}].id`, funnel.id);
      setValue(`funnelSelection[${idx}].isActive`, false);
    });
  }, [funnels, setValue]);

  const handleStep1Submit = () => {
    if (Object.keys(errors).length === 0) {
      setActiveStep(2);
    }
  };

  const handleFunnelsData = () => {
    handleSubmit((data) => {
      const selectedFunnels = (data.funnelSelection || [])
        .filter((item) => item?.isActive && item?.id)
        .map((item) => item.id);

      if (!selectedFunnels.length) {
        setErrorFunnels(true);
        return;
      }

      setErrorFunnels(false);
      setValue("funnelIds", selectedFunnels);
      setActiveStep(3);
    })();
  };

  const handleSelectAllFunnels = () => {
    funnels.forEach((_, index) => {
      setValue(`funnelSelection[${index}].isActive`, true);
    });
  };

  const handleFinishSubmit = async (data) => {
    setIsSubmitting(true);
    setErrorQuery(false);

    try {
      await axios.post(ADMIN.NEW_REPRESENTATIVE, {
        customer: {
          ...data.customer,
          permissionId: "USER",
        },
        queries: data.queries || [],
        funnelIds: data.funnelIds || [],
      });

      reset();
      setActiveStep(4);
    } catch (error) {
      setErrorQuery(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedFunnelNames = (watch("funnelIds") || []).map((funnelId) => {
    const selectedFunnel = funnels.find((funnel) => funnel.id === funnelId);
    return selectedFunnel?.name || funnelId;
  });

  return (
    <section className="rep-form-shell">
      <header className="rep-form-header">
        <div>
          <h2 className="rep-form-title">Novo Representante</h2>
          <p className="rep-form-subtitle">
            Configure os dados de acesso, consultas e funis com um fluxo guiado.
          </p>
        </div>
        <div className="rep-form-pill">
          Etapa {Math.min(activeStep, 4)} de 4
        </div>
      </header>

      <ol className="rep-stepper">
        {STEPS.map((stepLabel, idx) => {
          const stepNumber = idx + 1;
          const isCurrent = activeStep === stepNumber;
          const isDone = activeStep > stepNumber;
          return (
            <li key={stepLabel} className="rep-step-item">
              <span
                className={`rep-step-dot ${
                  isDone ? "is-done" : isCurrent ? "is-current" : ""
                }`}
              >
                {isDone ? <FaCheckCircle size={12} /> : stepNumber}
              </span>
              <span className="rep-step-label">{stepLabel}</span>
            </li>
          );
        })}
      </ol>

      {activeStep === 1 && (
        <form onSubmit={handleSubmit(handleStep1Submit)} className="rep-panel">
          <h3 className="rep-panel-title">Informacoes do representante</h3>
          <div className="rep-grid-2 mb-4">
            <Input
              type="text"
              register={register}
              name="customer.name"
              label="Nome"
              required={true}
            />
            <div className="rep-field-wrap">
              <Input
                type="email"
                register={register}
                name="customer.email"
                label="E-mail"
                className="w-full"
                required={true}
                onBlur={onBlurEmailInfo}
              />
              {errors.customer?.email && (
                <small className="rep-error-inline">
                  {errors.customer.email.message}
                </small>
              )}
            </div>
          </div>

          <div className="rep-grid-2">
            <PhoneInput
              type="text"
              register={register}
              control={control}
              name="customer.phoneNumber"
              label="Celular"
              required={true}
              value={watch("customer.phoneNumber") || ""}
            />
            <Input
              type="password"
              register={register}
              name="customer.password"
              label="Senha"
              className="w-full"
              required={true}
            />
          </div>

          <div className="rep-actions center">
            <button className="rep-btn primary" type="submit">
              Próximo
            </button>
          </div>
        </form>
      )}

      {activeStep === 2 && (
        <form className="rep-panel">
          <div className="rep-panel-top">
            <div>
              <h3 className="rep-panel-title">Selecione os funis com acesso</h3>
              <p className="rep-panel-help">
                O representante so visualiza os funis selecionados.
              </p>
            </div>
            <div className="rep-top-actions">
              <span className="rep-counter">
                {selectedFunnelNames.length} funis
              </span>
              <button
                type="button"
                className="rep-btn ghost"
                onClick={handleSelectAllFunnels}
              >
                Selecionar todos
              </button>
            </div>
          </div>

          {funnels.length > 0 ? (
            <>
              <div className="rep-funnel-grid">
                {funnels.map((funnel, index) => (
                  <label key={funnel.id} className="rep-funnel-card">
                    <input
                      type="checkbox"
                      {...register(`funnelSelection[${index}].isActive`)}
                    />
                    <span>{funnel.name}</span>
                    <Input
                      type="hidden"
                      register={register}
                      name={`funnelSelection[${index}].id`}
                    />
                  </label>
                ))}
              </div>

              {errorFunnels && (
                <small className="rep-error-inline block">
                  Selecione ao menos um funil para o representante.
                </small>
              )}

              <div className="rep-actions">
                <button
                  type="button"
                  className="rep-btn muted"
                  onClick={() => setActiveStep(1)}
                >
                  Voltar
                </button>
                <button
                  type="button"
                  className="rep-btn primary"
                  onClick={handleFunnelsData}
                >
                  Próximo
                </button>
              </div>
            </>
          ) : (
            <div className="rep-empty">
              Nao ha funis disponiveis para selecao.
            </div>
          )}
        </form>
      )}

      {activeStep === 3 && (
        <form onSubmit={handleSubmit(handleFinishSubmit)} className="rep-panel">
          <h3 className="rep-panel-title">Revise as informações</h3>

          <div className="rep-summary-grid">
            <div className="rep-summary-item">
              <small>Nome</small>
              <strong>{watch("customer.name") || "-"}</strong>
            </div>
            <div className="rep-summary-item">
              <small>E-mail</small>
              <strong>{watch("customer.email") || "-"}</strong>
            </div>
            <div className="rep-summary-item">
              <small>Celular</small>
              <strong>{watch("customer.phoneNumber") || "-"}</strong>
            </div>
          </div>

          <div className="rep-kpis">
            <div className="rep-kpi">
              <span>Funis com acesso</span>
              <strong>{selectedFunnelNames.length}</strong>
            </div>
          </div>

          <div className="rep-selected-funnels">
            <p>Funis selecionados</p>
            {selectedFunnelNames.length > 0 ? (
              <ul>
                {selectedFunnelNames.map((name, idx) => (
                  <li key={`${name}-${idx}`}>{name}</li>
                ))}
              </ul>
            ) : (
              <div className="rep-empty-inline">Nenhum funil selecionado.</div>
            )}
          </div>

          <div className="rep-actions">
            <button
              type="button"
              className="rep-btn muted"
              onClick={() => setActiveStep(2)}
            >
              Voltar
            </button>
            <button
              type="submit"
              className="rep-btn primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Finalizando..." : "Finalizar"}
            </button>
          </div>

          {errorQuery && (
            <div className="rep-error-box">
              Erro ao tentar cadastrar o representante.
            </div>
          )}
        </form>
      )}

      {activeStep === 4 && (
        <div className="rep-success-box">
          <div className="rep-success-icon">
            <FaCheckCircle size={30} />
          </div>
          <h3>Representante cadastrado com sucesso!</h3>
          <p>
            O acesso foi configurado conforme consultas e funis selecionados.
          </p>
        </div>
      )}
    </section>
  );
};

export default AdminRepresentativeForm;
